import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { productCases, openSourceProjects } from '@/content/portfolio'
import { blogPosts } from '@/content/blogs'
import { ProjectDiagram } from '@/components/sections/cv/ProjectDiagram'
import { ProjectShowcase } from '@/components/ui/project-showcase'
import AsciiAnimation from '@/components/ui/AsciiAnimation'

export function FeaturedWork() {
  const featured = ['lumberworks-platform', 'vietcetera-platform', 'multi-agent-evaluation', '96booth']
    .map((id) => productCases.find((project) => project.id === id)!)
  const pmKit = openSourceProjects[0]

  return (
    <section id="selected-work" className="border-t border-vibe-border py-16 lg:py-24">
      <div className="mx-auto max-w-6xl px-4 lg:px-8">
        <div className="section-label mb-3 text-accent-primary">Selected work</div>
        <h2 className="text-3xl lg:text-4xl font-medium tracking-tight text-text-heading">Products, platforms & tools</h2>
        <p className="mt-4 max-w-2xl text-base text-text-secondary">A selection of employer products, AI work, and independent builds.</p>
        <div className="mt-8">
          <ProjectShowcase projects={[
            ...featured.map((project) => ({
              title: project.title,
              description: project.overview,
              category: project.category,
              period: project.period?.match(/\d{4}/g)?.join('–'),
              link: `/work#${project.id}`,
              preview: project.image
                ? <img src={project.image} alt={project.imageAlt} width={1280} height={720} loading="lazy" />
                : <ProjectDiagram visual="playstat" />,
            })),
            {
              title: 'PM-Kit',
              description: pmKit.description,
              category: 'Open source',
              link: '/work#open-source',
              preview: <img src={pmKit.image} alt="PM-Kit public repository" width={1280} height={720} loading="lazy" />,
            },
          ]} />
        </div>
        <Link to="/work" className="mt-8 inline-flex items-center gap-2 text-base text-accent-primary underline underline-offset-4 hover:text-text-primary">View all work <ArrowRight className="h-4 w-4" aria-hidden="true" /></Link>
      </div>
    </section>
  )
}

export function CareerSnapshot() {
  return (
    <section className="border-t border-vibe-border py-16 lg:py-24">
      <div className="mx-auto max-w-6xl px-4 lg:px-8">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="max-w-2xl">
            <div className="section-label mb-3">Career</div>
            <h2 className="text-3xl lg:text-4xl font-medium tracking-tight text-text-heading">Engineering → product ownership → leadership</h2>
          </div>
          <div className="pointer-events-none h-48 w-full shrink-0 sm:w-64" aria-hidden="true">
            <AsciiAnimation frameFolder="animations/computer" frameCount={78} quality="medium" fps={28} color="rgb(var(--accent))" />
          </div>
        </div>
        <ol className="mt-8 grid gap-6 md:grid-cols-3">
          {[
            ['2017–2020', 'Software engineering', 'BLISS · ALAX.io · Axon Active', 'Built customer-facing web and mobile products, publisher tools, and fintech interfaces.'],
            ['2020–2025', 'Technical product management', 'Vietcetera Media', 'Led the public website rebuild, mobile app delivery, internal platforms, and data reporting.'],
            ['2025–2026', 'Technical PM → Head of Product', 'Lumberworks', 'Owned 0-to-1 product tracks, white-label delivery, roadmaps, and client-facing AI work.'],
          ].map(([period, title, company, description]) => (
            <li key={period} className="border-l border-accent-primary/50 pl-5">
              <div className="font-mono text-xs text-accent-primary">{period}</div>
              <h3 className="mt-3 text-xl font-medium text-text-heading">{title}</h3>
              <p className="mt-2 text-sm text-text-primary">{company}</p>
              <p className="mt-3 text-sm leading-relaxed text-text-secondary">{description}</p>
            </li>
          ))}
        </ol>
        <Link to="/about#experience" className="mt-8 inline-block text-base text-accent-primary underline underline-offset-4 hover:text-text-primary">Full experience & technical background</Link>
      </div>
    </section>
  )
}

export function WorkingMethod() {
  const creative = productCases.find((project) => project.id === 'creative-ai-workspace')!
  return (
    <section className="border-t border-vibe-border py-16 lg:py-24">
      <div className="mx-auto grid max-w-6xl gap-8 px-4 lg:grid-cols-2 lg:px-8">
        <div>
          <div className="section-label mb-3">How I work</div>
          <h2 className="text-3xl lg:text-4xl font-medium tracking-tight text-text-heading">Make the decision clear, then build</h2>
          <p className="mt-5 text-base leading-relaxed text-text-secondary">I start with users, workflows, and constraints. I use prototypes to make trade-offs concrete, then work with engineers through delivery. AI helps me research, build, and check; I remain responsible for the decisions.</p>
          <Link to="/about#ai-workflows" className="mt-6 inline-block text-base text-accent-primary underline underline-offset-4 hover:text-text-primary">More about my working method</Link>
        </div>
        <div className="rounded-xl border border-vibe-border bg-card-bg p-6">
          <div className="section-label text-accent-primary">In practice · {creative.title}</div>
          <p className="mt-4 text-base leading-relaxed text-text-primary">{creative.decision}</p>
          <p className="mt-3 text-sm leading-relaxed text-text-secondary">{creative.outcome}</p>
          <Link to="/work#creative-ai-workspace" className="mt-5 inline-block text-sm text-accent-primary underline underline-offset-4 hover:text-text-primary">Explore the workflow</Link>
        </div>
      </div>
    </section>
  )
}

export function FeaturedWriting() {
  const posts = [...blogPosts].sort((a, b) => b.date.localeCompare(a.date)).slice(0, 3)
  return (
    <section className="border-t border-vibe-border py-16 lg:py-24">
      <div className="mx-auto max-w-6xl px-4 lg:px-8">
        <div className="section-label mb-3">Writing</div>
        <h2 className="text-3xl lg:text-4xl font-medium tracking-tight text-text-heading">Recent notes</h2>
        <p className="mt-4 text-base text-text-secondary">Ideas on AI, product work, and learning. Posts are in English and Vietnamese.</p>
        <div className="mt-8 grid gap-5 md:grid-cols-3">
          {posts.map((post) => (
            <Link key={post.id} to={`/blog/${post.slug}`} className="rounded-xl border border-vibe-border bg-card-bg p-5 transition-colors hover:border-accent-primary/50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-accent-primary">
              <div className="font-mono text-xs text-text-muted">{new Intl.DateTimeFormat('en', { month: 'short', day: 'numeric', year: 'numeric' }).format(new Date(post.date))} · {post.lang === 'vi' ? 'Vietnamese' : 'English'}</div>
              <h3 lang={post.lang} className="mt-4 text-xl font-medium leading-snug text-text-heading">{post.lang === 'vi' ? post.firstLineVi.replace(/^#+\s*/, '') : post.titleEn}</h3>
              <span className="mt-5 inline-flex items-center gap-2 text-sm text-accent-primary">Read note <ArrowRight className="h-4 w-4" aria-hidden="true" /></span>
            </Link>
          ))}
        </div>
        <Link to="/blog" className="mt-8 inline-block text-base text-accent-primary underline underline-offset-4 hover:text-text-primary">Browse all writing</Link>
      </div>
    </section>
  )
}
